if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500), {passive: true});
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false), {passive: true});
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}

if (!customElements.get('ml-media-gallery')) {
  /**
   * GH GALLERY v3 (2026-09-22)
   * - variant-aware: shows the selected variant's own photos + shared photos
   * - vertical thumbnail rail (desktop) / strip (mobile) with active-thumb tracking
   * - hover-to-zoom on desktop, swipe on touch, arrow keys, live "n / total" counter
   * - lazy loads everything except the active image; preloads neighbours
   * Public API kept for variants.js / product-modal: setActiveMedia(el, index), goDirection(isNext), index, elements.thumbnails
   */
  customElements.define('ml-media-gallery', class MLMediaGallery extends HTMLElement {
    constructor() {
      super();
      this.index = 0;
      this.showAll = false;
      this.currentVariant = this.dataset.currentVariant || null;
      this.sectionId = this.dataset.section || (this.id || '').replace('ML_Media_Gallery_', '');
      this.elements = {
        viewer: this.querySelector('[id^="ML_Media_Gallery_Viewer"]'),
        thumbnails: Array.from(this.querySelectorAll('ml-media-gallery-thumbnail, [id^="ML_Media_Gallery_Thumbnail_"]')),
        holder: this.querySelector('.ML_Media_Gallery_Thumbnail_Holder'),
        rail: this.querySelector('.gh-thumb-rail'),
        counter: this.querySelector('.gh-gallery-counter'),
        showAll: this.querySelector('.gh-gallery-showall'),
        hint: this.querySelector('.gh-zoom-hint'),
      };
      if (!this.elements.viewer) return;
      this.items = Array.from(this.elements.viewer.querySelectorAll('.ml_nested_selection'));
      this.elements.thumbnails = this.elements.thumbnails.filter((t) => t.tagName === 'ML-MEDIA-GALLERY-THUMBNAIL');

      this.initVariantMap();
      this.bindViewer();
      this.bindThumbnails();
      this.bindRail();
      this.bindVariantChanges();

      this.applyFilter();
      const shown = this.items.find((el) => el.classList.contains('show')) || this.visibleItems()[0];
      this.setActiveMedia(null, shown ? Number(shown.dataset.mediaIndex) : 0, { silent: true });
    }

    /* ---------- variant awareness ---------- */
    initVariantMap() {
      this.mediaVariants = new Map();
      let tagged = 0;
      const variants = new Set();
      this.items.forEach((el) => {
        const ids = (el.dataset.variants || '').split(',').map((v) => v.trim()).filter(Boolean);
        this.mediaVariants.set(String(el.dataset.mediaId), ids);
        if (ids.length) {
          tagged += 1;
          ids.forEach((v) => variants.add(v));
        }
      });
      this.filterCapable = tagged >= 2 && variants.size >= 2;
    }

    idsFor(el) {
      return this.mediaVariants.get(String(el.dataset.mediaId || el.dataset.id)) || [];
    }

    variantHasOwnMedia(vid) {
      return this.items.some((el) => this.idsFor(el).includes(String(vid)));
    }

    applyFilter() {
      const vid = this.currentVariant ? String(this.currentVariant) : null;
      const hasOwn = !!(this.filterCapable && vid && this.variantHasOwnMedia(vid));
      const active = hasOwn && !this.showAll;
      const isHidden = (ids) => active && ids.length > 0 && !ids.includes(vid);

      this.items.forEach((el, i) => {
        const hide = isHidden(this.idsFor(el));
        el.toggleAttribute('hidden', hide);
        const thumb = this.elements.thumbnails[i];
        if (thumb) thumb.toggleAttribute('hidden', hide);
      });

      const modal = document.getElementById(`ProductModal-${this.sectionId}`);
      modal?.querySelectorAll('.product-media-modal__content > [data-media-id], .product-media-modal__content > * [data-media-id]').forEach((el) => {
        const wrap = el.closest('.product-media-modal__content > *') || el;
        wrap.toggleAttribute('hidden', isHidden(this.mediaVariants.get(String(el.dataset.mediaId)) || []));
      });

      this.dataset.filtered = active ? 'true' : 'false';
      if (this.elements.showAll) {
        this.elements.showAll.hidden = !hasOwn;
        this.elements.showAll.textContent = this.showAll
          ? (this.elements.showAll.dataset.labelLess || 'Show fewer photos')
          : (this.elements.showAll.dataset.labelAll || 'View all photos');
        this.elements.showAll.setAttribute('aria-pressed', this.showAll ? 'true' : 'false');
      }
      this.updateCounter();
      this.updateRail();
    }

    setVariant(vid) {
      if (!vid) return;
      vid = String(vid);
      const changed = vid !== this.currentVariant;
      this.currentVariant = vid;
      if (changed) this.showAll = false;
      this.applyFilter();
      const current = this.items[this.index];
      // variants.js already switched to the variant's featured media before this runs: keep it
      if (current && !current.hasAttribute('hidden') && (!changed || this.idsFor(current).includes(vid))) {
        this.scrollThumbIntoView(this.elements.thumbnails[this.index]);
        this.updateCounter();
        return;
      }
      const own = this.visibleItems().find((el) => this.idsFor(el).includes(vid));
      const next = own || this.visibleItems()[0];
      if (next) this.setActiveMedia(null, Number(next.dataset.mediaIndex));
    }

    bindVariantChanges() {
      // variants.js dispatches a bubbling `change` on the product form's hidden id input
      document.addEventListener('change', (event) => {
        const input = event.target;
        if (!input || input.name !== 'id' || input.tagName !== 'INPUT') return;
        const form = input.form || input.closest('form');
        // note: form.id is shadowed by the <input name="id"> itself, so read the attribute
        const formId = form ? (form.getAttribute('id') || '') : '';
        if (formId && this.sectionId && !formId.includes(this.sectionId)) return;
        if (input.value && input.value !== this.currentVariant) this.setVariant(input.value);
      });
      this.elements.showAll?.addEventListener('click', () => {
        this.showAll = !this.showAll;
        this.applyFilter();
        const current = this.items[this.index];
        if (!current || current.hasAttribute('hidden')) {
          const first = this.visibleItems()[0];
          if (first) this.setActiveMedia(null, Number(first.dataset.mediaIndex));
        } else {
          this.scrollThumbIntoView(this.elements.thumbnails[this.index]);
        }
      });
    }

    /* ---------- viewer: arrows, swipe, zoom, keys ---------- */
    bindViewer() {
      const viewer = this.elements.viewer;
      viewer.querySelector('[id^="product_media_next_"]')?.addEventListener('click', () => this.goDirection(true), { passive: true });
      viewer.querySelector('[id^="product_media_previous_"]')?.addEventListener('click', () => this.goDirection(false), { passive: true });

      // swipe (touch / pen)
      viewer.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return;
        this.swipe = { x: e.clientX, y: e.clientY };
      }, { passive: true });
      viewer.addEventListener('pointerup', (e) => {
        if (!this.swipe) return;
        const dx = e.clientX - this.swipe.x;
        const dy = e.clientY - this.swipe.y;
        this.swipe = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClick = true;
          this.goDirection(dx < 0);
          setTimeout(() => { this.suppressClick = false; }, 400);
        }
      }, { passive: true });
      viewer.addEventListener('pointercancel', () => { this.swipe = null; }, { passive: true });
      viewer.addEventListener('click', (e) => {
        if (this.suppressClick) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);

      // hover zoom (desktop only)
      this.canZoom = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (this.canZoom) {
        viewer.addEventListener('mousemove', (e) => {
          if (e.target.closest('.product_media_next, .product_media_previous')) { this.stopZoom(); return; }
          const item = this.items[this.index];
          const img = item && item.querySelector('img.ml_gallery_thumbnail_main');
          if (!img || item.dataset.mediaType && item.dataset.mediaType !== 'image') return;
          const rect = viewer.getBoundingClientRect();
          const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
          const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
          item.style.setProperty('--gh-zx', `${x.toFixed(2)}%`);
          item.style.setProperty('--gh-zy', `${y.toFixed(2)}%`);
          if (!item.classList.contains('is-zooming')) {
            item.classList.add('is-zooming');
            this.elements.hint?.classList.add('is-hidden');
          }
        }, { passive: true });
        viewer.addEventListener('mouseleave', () => this.stopZoom(), { passive: true });
      } else {
        this.elements.hint?.classList.add('is-hidden');
      }

      // keyboard
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.goDirection(true); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.goDirection(false); }
      });
    }

    stopZoom() {
      this.items.forEach((el) => el.classList.remove('is-zooming'));
    }

    /* ---------- thumbnails + rail ---------- */
    bindThumbnails() {
      this.elements.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.setActiveMedia(thumb, Number(thumb.dataset.index)), { passive: true });
      });
    }

    bindRail() {
      const { rail, holder } = this.elements;
      if (!rail || !holder) return;
      this.railPrev = rail.querySelector('.gh-thumb-nav--prev');
      this.railNext = rail.querySelector('.gh-thumb-nav--next');
      const step = (dir) => {
        const vertical = this.railIsVertical();
        const amount = (vertical ? holder.clientHeight : holder.clientWidth) * 0.8 * dir;
        holder.scrollBy({ top: vertical ? amount : 0, left: vertical ? 0 : amount, behavior: 'smooth' });
      };
      this.railPrev?.addEventListener('click', () => step(-1), { passive: true });
      this.railNext?.addEventListener('click', () => step(1), { passive: true });
      holder.addEventListener('scroll', () => this.updateRailButtons(), { passive: true });
      const refresh = () => this.updateRail();
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(refresh);
        ro.observe(holder);
        ro.observe(rail);
      }
      window.addEventListener('resize', refresh, { passive: true });
      window.addEventListener('load', refresh, { passive: true });
      holder.querySelectorAll('img').forEach((img) => img.addEventListener('load', refresh, { once: true, passive: true }));
      requestAnimationFrame(refresh);
      setTimeout(refresh, 400);
    }

    railIsVertical() {
      const holder = this.elements.holder;
      return !!holder && getComputedStyle(holder).flexDirection === 'column';
    }

    updateRail() {
      const { rail, holder } = this.elements;
      if (!rail || !holder) return;
      const vertical = this.railIsVertical();
      const overflow = vertical ? holder.scrollHeight > holder.clientHeight + 2 : holder.scrollWidth > holder.clientWidth + 2;
      rail.classList.toggle('has-overflow', overflow);
      this.updateRailButtons();
    }

    updateRailButtons() {
      const holder = this.elements.holder;
      if (!holder || !this.railPrev) return;
      const vertical = this.railIsVertical();
      const pos = vertical ? holder.scrollTop : holder.scrollLeft;
      const max = vertical ? holder.scrollHeight - holder.clientHeight : holder.scrollWidth - holder.clientWidth;
      this.railPrev.classList.toggle('is-disabled', pos <= 1);
      this.railNext.classList.toggle('is-disabled', pos >= max - 1);
    }

    scrollThumbIntoView(thumb) {
      const holder = this.elements.holder;
      if (!holder || !thumb) return;
      if (this.railIsVertical()) {
        const top = thumb.offsetTop - (holder.clientHeight - thumb.offsetHeight) / 2;
        holder.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      } else {
        const left = thumb.offsetLeft - (holder.clientWidth - thumb.offsetWidth) / 2;
        holder.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    /* ---------- navigation ---------- */
    visibleItems() {
      return this.items.filter((el) => !el.hasAttribute('hidden'));
    }

    goDirection(isNext) {
      const visible = this.visibleItems();
      if (!visible.length) return;
      let pos = visible.findIndex((el) => Number(el.dataset.mediaIndex) === Number(this.index));
      if (pos < 0) pos = 0;
      pos = isNext ? (pos + 1) % visible.length : (pos - 1 + visible.length) % visible.length;
      this.setActiveMedia(null, Number(visible[pos].dataset.mediaIndex));
    }

    setActiveMedia(el, index, opts = {}) {
      index = Number(index) || 0;
      const target = this.items.find((item) => Number(item.dataset.mediaIndex) === index) || this.items[index];
      if (!target) return;
      if (target.hasAttribute('hidden')) target.removeAttribute('hidden');
      this.index = index;
      this.stopZoom();
      if (window.pauseAllMedia) window.pauseAllMedia();

      this.items.forEach((item) => {
        const isActive = item === target;
        item.classList.toggle('show', isActive);
        item.classList.toggle('hide', !isActive);
        if (isActive) {
          const img = item.querySelector('img[loading="lazy"]');
          if (img) img.loading = 'eager';
          this.playActiveMedia(item);
        } else {
          item.querySelectorAll('iframe').forEach((frame) => {
            frame.contentWindow?.postMessage?.('{"event":"command","func":"pauseVideo","args":""}', '*');
          });
        }
      });

      this.elements.thumbnails.forEach((thumb) => {
        const isActive = Number(thumb.dataset.index) === index;
        thumb.classList.toggle('ml_thumb_selected', isActive);
        const btn = thumb.querySelector('button');
        if (btn) {
          if (isActive) btn.setAttribute('aria-current', 'true');
          else btn.removeAttribute('aria-current');
        }
        if (isActive) this.scrollThumbIntoView(thumb);
      });

      this.updateCounter();
      this.preloadNeighbours();

      // keep the viewer on screen when the switch came from further down the page
      if (!opts.silent && el && el.classList && !el.classList.contains('ML_Media_Gallery_Thumbnail')) {
        this.elements.viewer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    preloadNeighbours() {
      const visible = this.visibleItems();
      const pos = visible.findIndex((el) => Number(el.dataset.mediaIndex) === Number(this.index));
      if (pos < 0) return;
      [visible[pos + 1], visible[pos - 1]].forEach((item) => {
        const img = item && item.querySelector('img[loading="lazy"]');
        if (img) img.loading = 'eager';
      });
    }

    updateCounter() {
      const counter = this.elements.counter;
      if (!counter) return;
      const visible = this.visibleItems();
      const pos = visible.findIndex((el) => Number(el.dataset.mediaIndex) === Number(this.index));
      const current = counter.querySelector('.gh-gallery-counter__current');
      const total = counter.querySelector('.gh-gallery-counter__total');
      if (current) current.textContent = String(Math.max(1, pos + 1));
      if (total) total.textContent = String(visible.length);
      counter.hidden = visible.length <= 1;
    }

    playActiveMedia(activeItem, focus = false) {
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') deferredMedia.loadContent(focus);
    }

    /* legacy helpers kept for anything still calling them */
    clearSelectedThumbs() {
      this.elements.thumbnails.forEach((thumb) => thumb.classList.remove('ml_thumb_selected'));
    }
    deafultSelectedThumbnail(index = null) {
      const thumb = this.elements.thumbnails[Number(index) || this.index];
      if (thumb) thumb.classList.add('ml_thumb_selected');
    }
  });
}
